import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { getOptimizedImageUrl } from '../lib/seo';

export default function Gallery({ images, title }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const [visibleCount, setVisibleCount] = useState(16);

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    if (activeIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setActiveIndex(null);
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex]);

  if (!images || images.length === 0) return null;

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleShowMore = () => {
    setVisibleCount((prev) => Math.min(prev + 16, images.length));
  };

  return (
    <div className="space-y-8">
      {title && (
        <div className="border-l-4 border-amber-400 pl-4">
          <h2 className="text-2xl font-bold text-slate-900 pt-6">{title}</h2>
          <p className="text-sm text-slate-500 mt-1">Klik på et billede for at se det i stor visning</p>
        </div>
      )}

      {/* Image Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.slice(0, visibleCount).map((src, index) => (
          <div
            key={index}
            onClick={() => setActiveIndex(index)}
            className="group relative aspect-[4/3] overflow-hidden rounded-xl bg-slate-100 cursor-pointer border border-slate-100 shadow-sm"
          >
            <img
              src={getOptimizedImageUrl(src, { width: 600, quality: 80 })}
              alt={`${title || 'BM Maler'} - Malermester i Vejle og Trekantsområdet - Billede ${index + 1}`}
              width="600"
              height="450"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              decoding="async"
            />
            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <div className="p-3 rounded-full bg-white/20 backdrop-blur-sm text-white scale-90 group-hover:scale-100 transition-transform duration-300">
                <ZoomIn className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Show More Button */}
      {visibleCount < images.length && (
        <div className="flex justify-center pt-4">
          <button
            onClick={handleShowMore}
            className="inline-flex items-center justify-center px-8 py-3.5 border border-slate-200 hover:border-[#F1B528] text-slate-800 hover:text-[#D99C1B] bg-white hover:bg-slate-50 rounded-full text-sm font-bold shadow-sm transition-all duration-300 cursor-pointer"
          >
            Vis flere billeder ({images.length - visibleCount} tilbage)
          </button>
        </div>
      )}

      {/* Lightbox Modal */}
      {activeIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/95 backdrop-blur-sm select-none">
          {/* Close Area */}
          <div className="absolute inset-0" onClick={() => setActiveIndex(null)} />

          {/* Close Button */}
          <button
            onClick={() => setActiveIndex(null)}
            className="absolute top-6 right-6 z-50 p-3 rounded-full bg-white/10 hover:bg-amber-400 hover:text-slate-950 text-white transition-all duration-200 cursor-pointer"
            aria-label="Luk visning"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Navigation - Prev */}
          <button
            onClick={handlePrev}
            className="absolute left-4 sm:left-6 z-50 p-3 rounded-full bg-white/10 hover:bg-amber-400 hover:text-slate-950 text-white transition-all duration-200 cursor-pointer"
            aria-label="Forrige billede"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Active Image Container */}
          <div className="relative max-w-[90vw] max-h-[80vh] z-10 flex flex-col items-center">
            <img
              src={getOptimizedImageUrl(images[activeIndex], { width: 1200, quality: 85 })}
              alt={`${title || 'BM Maler Galleri'} - Stor visning`}
              className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-2xl"
            />
            {/* Image Counter */}
            <div className="mt-4 text-sm font-semibold text-white bg-slate-900/80 px-4 py-1.5 rounded-full border border-slate-800">
              {activeIndex + 1} / {images.length}
            </div>
          </div>

          {/* Navigation - Next */}
          <button
            onClick={handleNext}
            className="absolute right-4 sm:right-6 z-50 p-3 rounded-full bg-white/10 hover:bg-amber-400 hover:text-slate-950 text-white transition-all duration-200 cursor-pointer"
            aria-label="Næste billede"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
}
